import type { ThemeColor } from "@/colors";
import { computed, defineComponent } from "vue";
import { cn } from "@/utils/cn";
import { p } from "@/utils/props-builder";
import Icon from "../icon/Icon";

const alertTypes = {
  info: {
    icon: "lucide:info",
    color: "blue",
  },
  success: {
    icon: "lucide:check",
    color: "green",
  },
  warning: {
    icon: "lucide:alert-triangle",
    color: "amber",
  },
  error: {
    icon: "lucide:alert-circle",
    color: "red",
  },
} satisfies Record<string, { icon: string; color: ThemeColor }>;

export type AlertType = keyof typeof alertTypes;

export default defineComponent({
  name: "Alert",
  props: {
    title: p.$string(),
    description: p.string(),
    type: p.string<AlertType>("info"),
    showIcon: p.boolean(true),
    variant: p.string<"default" | "colorful">("default"),
    size: p.string<"sm" | "md" | "lg">("md"),
  },
  setup(props, { slots }) {
    const icon = computed(() => alertTypes[props.type]?.icon || alertTypes.info.icon);
    const color = computed(() => alertTypes[props.type]?.color || alertTypes.info.color);

    const description = computed(() => {
      return props.description || slots.default?.() || "";
    });

    const sizeClasses = {
      sm: {
        padding: "px-3 py-2",
        gap: "gap-2",
        icon: "size-3.5 mt-1",
        iconMargin: "mt-0",
        titleText: "text-xs",
        descriptionText: "text-xs",
      },
      md: {
        padding: "px-4 py-3",
        gap: "gap-2.5",
        icon: "size-4",
        iconMargin: "mt-0.5",
        titleText: "text-sm",
        descriptionText: "text-sm",
      },
      lg: {
        padding: "px-5 py-4",
        gap: "gap-3",
        icon: "size-5",
        iconMargin: "mt-0.5",
        titleText: "text-base",
        descriptionText: "text-sm",
      },
    };

    const currentSize = computed(() => sizeClasses[props.size]);

    return () => (
      <div
        class={cn(
          "rounded-md border",
          currentSize.value.padding,
          props.variant === "colorful" && "border-accent-6 bg-accent-1A"
        )}
        data-accent={color.value}
      >
        <div class={cn("flex", currentSize.value.gap)}>
          {props.showIcon && (
            <Icon
              icon={icon.value}
              class={cn(
                currentSize.value.iconMargin,
                currentSize.value.icon,
                "shrink-0 text-accent-9 opacity-60"
              )}
              aria-hidden="true"
              data-accent={color.value}
            />
          )}
          <div class="grow space-y-1">
            <div class="flex grow justify-between gap-3">
              <p
                data-accent={color.value}
                class={cn(
                  currentSize.value.titleText,
                  !!description.value && "font-medium",
                  props.variant === "colorful" ? "text-accent-11" : "text-foreground"
                )}
              >
                {props.title}
              </p>
              {slots.$button?.()}
            </div>
            <div
              v-show={!!description.value}
              class={cn(currentSize.value.descriptionText, "text-muted-foreground")}
            >
              {description.value || slots.default?.()}
            </div>
          </div>
        </div>
      </div>
    );
  },
});
